import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import {
  DEST_STATUS_COLOR,
  DEST_STATUS_LABEL,
  formatDateBr,
  formatDateTimeBr,
} from './juridicoUtils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Aba Distribuição + Controle de retorno (Fatia B).
 */
export default function JuridicoDistribuicaoPanel({ oficio, onUpdated }) {
  const { user, isAdmin, rbacRole } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [internos, setInternos] = useState([]);
  const [externos, setExternos] = useState([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [corpo, setCorpo] = useState(oficio?.rascunhoEmailIa || '');
  const [anexarOriginal, setAnexarOriginal] = useState(true);
  const [anexarResumo, setAnexarResumo] = useState(Boolean(oficio?.documentoResumoPath));
  const [enviando, setEnviando] = useState(false);
  const [cobrandoId, setCobrandoId] = useState(null);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');

  const isStaff =
    oficio?.isJuridicoStaff ||
    isAdmin ||
    rbacRole === 'SUPER_ADMIN' ||
    rbacRole === 'LOCAL_ADMIN';

  useEffect(() => {
    setCorpo(oficio?.rascunhoEmailIa || corpo || '');
    setAnexarResumo(Boolean(oficio?.documentoResumoPath));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oficio?.id, oficio?.rascunhoEmailIa, oficio?.documentoResumoPath]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/usuarios');
        const data = await res.json().catch(() => []);
        if (res.ok && Array.isArray(data)) setUsuarios(data.filter((u) => u?.email));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, []);

  const destinatarios = oficio?.destinatarios || [];
  const distribuicoes = oficio?.distribuicoes || [];
  const encerrado = String(oficio?.status || '').toLowerCase() === 'encerrado';
  const meuDestId = oficio?.meuDestinatarioId || null;

  const isMeuDest = (d) =>
    Number(d.id) === Number(meuDestId) ||
    (user?.id && Number(d.usuarioId) === Number(user.id)) ||
    (user?.email &&
      String(d.email || '').toLowerCase() === String(user.email).toLowerCase());

  const opcoesUsuarios = useMemo(() => {
    const selectedIds = new Set(internos.map((u) => u.id));
    return usuarios.filter((u) => !selectedIds.has(u.id));
  }, [usuarios, internos]);

  const addExterno = () => {
    const email = emailDraft.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setErro('E-mail externo inválido.');
      return;
    }
    if (externos.includes(email) || internos.some((u) => String(u.email || '').toLowerCase() === email)) {
      setErro('E-mail já adicionado.');
      return;
    }
    setExternos((prev) => [...prev, email]);
    setEmailDraft('');
    setErro('');
  };

  const distribuir = async () => {
    setEnviando(true);
    setErro('');
    setMsg('');
    try {
      const res = await apiFetch(`/api/juridico/oficios/${oficio.id}/distribuir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          corpoEmail: corpo,
          anexarOriginal,
          anexarResumo,
          destinatariosInternos: internos.map((u) => ({
            usuarioId: u.id,
            email: u.email,
            nome: u.nome,
          })),
          destinatariosExternos: externos.map((email) => ({ email })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao distribuir');
      setMsg(
        data.email?.skipped
          ? 'Distribuição registrada (e-mail em modo manutenção ou sem SMTP).'
          : 'Distribuição enviada com sucesso.'
      );
      setInternos([]);
      setExternos([]);
      onUpdated?.(data);
    } catch (e) {
      setErro(e.message || 'Erro ao distribuir');
    } finally {
      setEnviando(false);
    }
  };

  const cobrar = async (destId) => {
    setCobrandoId(destId);
    setErro('');
    setMsg('');
    try {
      const res = await apiFetch(`/api/juridico/oficios/${oficio.id}/destinatarios/${destId}/cobrar`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha ao cobrar');
      setMsg('Cobrança registrada/enviada.');
      onUpdated?.({ ...oficio, destinatarios: data.destinatarios });
    } catch (e) {
      setErro(e.message || 'Erro ao cobrar');
    } finally {
      setCobrandoId(null);
    }
  };

  const anexarResposta = async (destId, file) => {
    if (!file) return;
    setErro('');
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      const res = await apiFetch(
        `/api/juridico/oficios/${oficio.id}/destinatarios/${destId}/anexar-resposta`,
        { method: 'POST', body: fd }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha no upload');
      setMsg('Resposta anexada.');
      onUpdated?.(data.oficio ? { ...data.oficio, destinatarios: data.destinatarios } : data);
    } catch (e) {
      setErro(e.message || 'Erro ao anexar');
    }
  };

  return (
    <Stack spacing={3}>
      {erro && (
        <Alert severity="error" onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}
      {msg && (
        <Alert severity="success" onClose={() => setMsg('')}>
          {msg}
        </Alert>
      )}

      {oficio?.podeResponder && (
        <Alert severity="info">
          Você tem pendência neste ofício. Use <strong>Anexar minha resposta</strong> na sua linha
          abaixo.
        </Alert>
      )}

      {!encerrado && isStaff && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
            Nova distribuição
          </Typography>
          <TextField
            label="Corpo do e-mail"
            fullWidth
            multiline
            minRows={6}
            value={corpo}
            onChange={(e) => setCorpo(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={anexarOriginal}
                  onChange={(e) => setAnexarOriginal(e.target.checked)}
                  disabled={!oficio?.arquivoOriginalPath}
                />
              }
              label="Anexar ofício original"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={anexarResumo}
                  onChange={(e) => setAnexarResumo(e.target.checked)}
                  disabled={!oficio?.documentoResumoPath}
                />
              }
              label="Anexar documento-resumo IA"
            />
          </Stack>

          <Autocomplete
            multiple
            options={opcoesUsuarios}
            getOptionLabel={(o) => `${o.nome || '—'} (${o.email})`}
            value={internos}
            onChange={(_, v) => setInternos(v)}
            renderInput={(params) => (
              <TextField {...params} label="Destinatários internos" placeholder="Buscar usuário…" />
            )}
            sx={{ mb: 2 }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField
              label="E-mail externo"
              fullWidth
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addExterno();
                }
              }}
              placeholder="nome@orgao.gov.br"
            />
            <Button variant="outlined" onClick={addExterno} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
              Adicionar
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {externos.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={() => setExternos((prev) => prev.filter((e) => e !== email))}
              />
            ))}
          </Stack>

          <Button
            variant="contained"
            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={enviando || !corpo.trim()}
            onClick={distribuir}
            sx={{ textTransform: 'none' }}
          >
            {enviando ? 'Enviando…' : 'Enviar distribuição'}
          </Button>
        </Paper>
      )}

      <Box>
        <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
          Controle de retorno
        </Typography>
        {destinatarios.length === 0 ? (
          <Typography color="text.secondary">
            Nenhum destinatário ainda. Ao criar o ofício com responsáveis, a distribuição automática
            gera as pendências aqui.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Destinatário</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notificação</TableCell>
                <TableCell>Resposta</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {destinatarios.map((d) => {
                const meu = isMeuDest(d);
                const podeAnexar = !encerrado && d.status !== 'respondido' && (meu || isStaff);
                return (
                <TableRow key={d.id} sx={meu ? { bgcolor: 'rgba(15,118,110,0.06)' } : undefined}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {d.usuarioNome || d.nomeExterno || '—'}
                      {meu ? ' (você)' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={DEST_STATUS_LABEL[d.status] || d.status}
                      color={DEST_STATUS_COLOR[d.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDateTimeBr(d.dataNotificacao)}</TableCell>
                  <TableCell>
                    {d.dataResposta ? formatDateTimeBr(d.dataResposta) : '—'}
                    {d.arquivoRespostaPath && (
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => openAuthenticatedUpload(d.arquivoRespostaPath)}
                        sx={{ textTransform: 'none', display: 'block' }}
                      >
                        Baixar
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {podeAnexar && (
                      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        {isStaff && !meu && (
                        <Button
                          size="small"
                          startIcon={
                            cobrandoId === d.id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <NotificationsActiveIcon />
                            )
                          }
                          disabled={cobrandoId === d.id}
                          onClick={() => cobrar(d.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Cobrar
                        </Button>
                        )}
                        <Button
                          size="small"
                          component="label"
                          variant={meu ? 'contained' : 'outlined'}
                          color={meu ? 'success' : 'primary'}
                          startIcon={<UploadFileIcon />}
                          sx={{ textTransform: 'none', fontWeight: meu ? 800 : 400 }}
                        >
                          {meu ? 'Anexar minha resposta' : 'Anexar'}
                          <input
                            type="file"
                            hidden
                            accept=".pdf,application/pdf,image/*"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = '';
                              anexarResposta(d.id, f);
                            }}
                          />
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        )}
        {String(oficio?.status) === 'pronto_para_resposta' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Todos os destinatários responderam — ofício pronto para resposta final / encerramento.
          </Alert>
        )}
      </Box>

      {distribuicoes.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Histórico de distribuições
          </Typography>
          <Stack spacing={1}>
            {distribuicoes.map((dist) => (
              <Paper key={dist.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTimeBr(dist.dataEnvio)} · {dist.enviadoPorNome || '—'} ·{' '}
                  {(dist.listaDestinatarios || []).map((x) => x.email).join(', ')}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {dist.corpoEmail}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {oficio?.dataLimiteResposta && (
        <Typography variant="caption" color="text.secondary">
          Prazo do ofício: {formatDateBr(oficio.dataLimiteResposta)}
        </Typography>
      )}
    </Stack>
  );
}
